import {
  addDays,
  addMonths,
  differenceInCalendarMonths,
  differenceInDays,
  getDate,
  getDaysInMonth,
  isBefore,
  isWeekend,
  setDate,
  subDays,
} from 'date-fns';
import type { EndDatesInterface, StartDatesInterface } from '@common/types';
import { coerceToDate, formatDate } from '@common/paymentsUtils/dateUtils';

export const DISPLAY_DATE_FORMAT = 'MMMM d, yyyy';
export const STORE_DATE_FORMAT = 'yyyy-MM-dd';

function adjustMonth(dayOfMonth: number, date: Date): Date {
  if (dayOfMonth < getDate(coerceToDate(date)!)) {
    date = addMonths(date, 1);
    date = setDate(date, dayOfMonth);
  }
  return date;
}

function adjustDay(dayOfMonth: number, date: Date): Date {
  const maxDay = getDaysInMonth(date);
  const dayOfMonthAdjusted = Math.min(maxDay, dayOfMonth);
  return setDate(date, dayOfMonthAdjusted);
}

function makeEndDates(dueDate: string | Date, monthCount: number): Array<EndDatesInterface> {
  const endDates: Array<EndDatesInterface> = [];
  while (monthCount >= 1) {
    let lastDueDate = addMonths(dueDate, monthCount);
    lastDueDate = subDays(lastDueDate, 1);
    const lastDueDateStr = formatDate(lastDueDate, DISPLAY_DATE_FORMAT);
    const noun = monthCount === 1 ? 'payment' : 'payments';
    endDates.push({
      date: formatDate(lastDueDate, STORE_DATE_FORMAT),
      payments: monthCount,
      display: `${lastDueDateStr} (${monthCount} ${noun})`,
    });
    monthCount -= 1;
  }
  return endDates;
}

function makeEndDatesWithCount(
  dueDate: string | Date,
  monthDifferenceFromFirstDueDate: number,
  monthToMonth: boolean,
  currentNumberOfPayments?: number,
): Array<EndDatesInterface> {
  const endDates: Array<EndDatesInterface> = [];
  const extendPaymentCount = 12 + ((currentNumberOfPayments || 0) - monthDifferenceFromFirstDueDate);
  const paymentsToRender = extendPaymentCount > 24 ? 24 : extendPaymentCount;
  let defaultMonthsToAdd = monthToMonth ? 12 : paymentsToRender;

  while (defaultMonthsToAdd >= 1) {
    const lastDayOfTerm = subDays(addMonths(dueDate, defaultMonthsToAdd), 1);
    const lastDayOfTermStr = formatDate(lastDayOfTerm, DISPLAY_DATE_FORMAT);
    const noun = monthDifferenceFromFirstDueDate + defaultMonthsToAdd === 1 ? 'payment' : 'payments';
    endDates.push({
      date: formatDate(lastDayOfTerm, STORE_DATE_FORMAT),
      paymentsToAdd: String(monthDifferenceFromFirstDueDate + defaultMonthsToAdd),
      display: `${lastDayOfTermStr} (${monthDifferenceFromFirstDueDate + defaultMonthsToAdd} ${noun})`,
    });
    defaultMonthsToAdd -= 1;
  }
  return endDates;
}

function wasStartDateAdjusted(startDate: string | Date, dayOfMonth: number): boolean {
  return dayOfMonth < 31 && getDaysInMonth(startDate) < dayOfMonth;
}

function makeStartDate(dueDate: string | Date, dayOfMonth: number, monthCount: number): StartDatesInterface {
  return {
    date: formatDate(dueDate, STORE_DATE_FORMAT),
    display: formatDate(dueDate, DISPLAY_DATE_FORMAT),
    wasAdjusted: wasStartDateAdjusted(dueDate, dayOfMonth),
    endDates: makeEndDates(dueDate, monthCount),
  };
}

export const makeDueDates = (
  dayOfMonth: number,
  firstDueDateString: string | Date,
  monthCount = 12,
): Array<StartDatesInterface> => {
  const datesToProvide = 4;
  let firstDueDate = coerceToDate(firstDueDateString) ?? new Date(firstDueDateString as any);
  firstDueDate = adjustMonth(dayOfMonth, firstDueDate);
  firstDueDate = adjustDay(dayOfMonth, firstDueDate);
  const startDates: Array<StartDatesInterface> = [makeStartDate(firstDueDate, dayOfMonth, monthCount)];

  const range: Array<number> = [...Array(datesToProvide - 1).keys()].map((value) => value + 1);
  range.forEach((monthsOffset: number) => {
    let nextDueDate = addMonths(firstDueDate, monthsOffset);
    nextDueDate = adjustDay(dayOfMonth, nextDueDate);
    startDates.push(makeStartDate(nextDueDate, dayOfMonth, monthCount));
  });
  return startDates;
};

const getFinalDueDate = (firstDueDate: Date, numberOfPayments: number): Date | null => {
  if (numberOfPayments == null) {
    return null;
  }
  if (numberOfPayments === 0) {
    return firstDueDate;
  }
  return addMonths(firstDueDate, numberOfPayments - 1);
};

export const makeEndDatesForUpdateTerm = ({
  today,
  dueDay,
  firstDueDateString,
  currentNumberOfPayments = 0,
  monthToMonth,
}: {
  today: Date;
  dueDay: number;
  firstDueDateString: string;
  currentNumberOfPayments?: number;
  monthToMonth: boolean;
}): Array<EndDatesInterface> => {
  const firstDueDate = coerceToDate(firstDueDateString) ?? new Date(firstDueDateString as any);
  const finalDueDateBasedOnFirstDueDate = getFinalDueDate(firstDueDate, currentNumberOfPayments);
  let calculatedDueDate: Date;
  let monthDifferenceFromFirstDueDate: number;
  if (finalDueDateBasedOnFirstDueDate != null && isBefore(finalDueDateBasedOnFirstDueDate, today)) {
    calculatedDueDate = addMonths(finalDueDateBasedOnFirstDueDate, 1);
    monthDifferenceFromFirstDueDate = currentNumberOfPayments;
  } else {
    calculatedDueDate = isBefore(firstDueDate, today) ? setDate(today, dueDay) : firstDueDate;
    monthDifferenceFromFirstDueDate = differenceInCalendarMonths(calculatedDueDate, firstDueDate);
  }
  return makeEndDatesWithCount(
    calculatedDueDate,
    monthDifferenceFromFirstDueDate,
    monthToMonth,
    currentNumberOfPayments,
  );
};

const MIN_TOTAL_DAYS = 5; // 3 business days plus 2 weekend days
const MIN_BUSINESS_DAYS = 3;
export const isStartDateTooSoon = (startDate: string | Date, today: string | Date): boolean => {
  const startDateInThePast = isBefore(startDate, today);
  if (startDateInThePast) {
    return true;
  }
  let diff = differenceInDays(startDate, today);
  if (diff > MIN_TOTAL_DAYS) {
    return false;
  }
  let currentDay = today;
  let businessDays = 0;
  while (diff > 0) {
    if (!isWeekend(currentDay)) {
      businessDays += 1;
    }
    diff -= 1;
    currentDay = addDays(currentDay, 1);
  }
  return businessDays <= MIN_BUSINESS_DAYS;
};
